import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, Archive, Calculator, Scale } from "lucide-react";
import { prodApi, fmtPct, errMsg } from "./prodApi";
import { useProd } from "./ProdApp";
import { PButton, PTitle, PCard, PBadge, PEmpty } from "./ProdUi";

export default function ProdRecipes() {
  const { recipes, user, reload } = useProd();
  const navigate = useNavigate();
  const canEdit = !!user?.is_manager;

  const groups = useMemo(() => {
    const m = {};
    recipes.forEach((r) => { (m[r.input_node_name || "—"] ||= []).push(r); });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [recipes]);

  const duplicate = async (r) => {
    try { const res = await prodApi.post(`/production/recipes/${r.id}/duplicate`); await reload(); toast.success("Ricetta duplicata"); navigate(`/produzione/ricette/${res.data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const archive = async (r) => {
    if (!window.confirm(`Archiviare la ricetta "${r.name}"? Le simulazioni e lavorazioni già salvate restano invariate.`)) return;
    try { await prodApi.delete(`/production/recipes/${r.id}`); await reload(); toast.success("Ricetta archiviata"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="prod-recipes-page">
      <PTitle kicker="Configurazione" title="Ricette di lavorazione" right={canEdit && <PButton variant="primary" onClick={() => navigate("/produzione/ricette/nuova")} data-testid="prod-recipe-new-btn"><Plus className="w-5 h-5" /> Nuova ricetta</PButton>}>
        La stessa materia prima può avere più ricette (intera, disosso completo, parziale, mista). Ogni modifica crea una nuova versione.
      </PTitle>
      {!recipes.length && <PEmpty title="Nessuna ricetta" text="Seleziona una materia prima di partenza (es. Pistola) e definisci i tagli in uscita con la loro resa teorica." />}
      <div className="space-y-6">
        {groups.map(([input, list]) => (
          <div key={input}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-2">Da: {input}</p>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {list.map((r) => {
                const sell = (r.outputs || []).filter((o) => ["sellable", "process", "reuse"].includes(o.disposal)).reduce((s, o) => s + (o.pct || 0), 0);
                return (
                  <PCard key={r.id} className="flex flex-col gap-3" data-testid={`prod-recipe-card-${r.code}`}>
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-['Chivo'] font-black text-lg text-white leading-tight">{r.name}</h3>
                        <PBadge tone={r.balanced ? "green" : "amber"} data-testid={`prod-recipe-balance-${r.code}`}>{fmtPct(r.pct_total, 1)}</PBadge>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{r.code} · v{r.version} · {(r.outputs || []).length} output · resa vendibile teorica <b className="text-slate-200">{fmtPct(sell, 1)}</b></p>
                      <p className="text-xs text-slate-500 mt-1">Manodopera {r.labour?.minutes || 0} min {r.labour?.basis === "per_kg_input" ? "/kg" : r.labour?.basis === "per_run" ? "/lavorazione" : "/pezzo"} · {r.labour?.hourly_cost || 0} €/h</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-auto">
                      <PButton size="sm" variant="primary" onClick={() => navigate(`/produzione/calcolatore?recipe=${r.id}`)} data-testid={`prod-recipe-simulate-${r.code}`}><Calculator className="w-4 h-4" /> Simula</PButton>
                      <PButton size="sm" variant="success" onClick={() => navigate(`/produzione/resa-rapida?recipe=${r.id}`)} data-testid={`prod-recipe-run-${r.code}`}><Scale className="w-4 h-4" /> Resa reale</PButton>
                      <PButton size="sm" onClick={() => navigate(`/produzione/ricette/${r.id}`)} className="col-span-2" data-testid={`prod-recipe-open-${r.code}`}>{canEdit ? "Apri / modifica" : "Apri"}</PButton>
                      {canEdit && <>
                        <PButton size="sm" onClick={() => duplicate(r)} data-testid={`prod-recipe-duplicate-${r.code}`}><Copy className="w-4 h-4" /> Duplica</PButton>
                        <PButton size="sm" variant="danger" onClick={() => archive(r)} data-testid={`prod-recipe-archive-${r.code}`}><Archive className="w-4 h-4" /> Archivia</PButton>
                      </>}
                    </div>
                  </PCard>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
